import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface SecondaryFormActionsProps {
  dirty: boolean;
  saving: boolean;
  dirtyLabel: string;
  cleanLabel: string;
  saveLabel: string;
  onSave: () => void;
  onReset: () => void;
  /** Blocks Save independently of `dirty` — a client-side validation failure elsewhere in the card
   * (e.g. the AI provider card's "model requires a provider" rule). Reset stays enabled. */
  saveDisabled?: boolean;
}

/**
 * Lightweight save/reset bar for a settings section that saves independently of
 * the page's primary `SettingsScaffold` sticky save bar (e.g. a page composed of
 * several independently-versioned resources, each with its own optimistic lock).
 */
export function SecondaryFormActions({
  dirty,
  saving,
  dirtyLabel,
  cleanLabel,
  saveLabel,
  onSave,
  onReset,
  saveDisabled = false,
}: SecondaryFormActionsProps) {
  const { t } = useTranslation('settings');
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      <Typography variant="body2" color={dirty ? 'text.primary' : 'text.secondary'}>
        {dirty ? dirtyLabel : cleanLabel}
      </Typography>
      <Button onClick={onReset} disabled={!dirty || saving}>
        {t('common:action.reset')}
      </Button>
      <Button
        variant="outlined"
        onClick={onSave}
        disabled={!dirty || saving || saveDisabled}
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
      >
        {saving ? t('common:status.saving') : saveLabel}
      </Button>
    </Box>
  );
}
